import React, { useState, useEffect } from 'react';
import { Search, Check, X, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaLicenciasDocentes() {
    const [licencias, setLicencias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalAction, setModalAction] = useState(''); // 'preaprobado' o 'observado'
    const [selectedLicencia, setSelectedLicencia] = useState(null);
    const [observacion, setObservacion] = useState('');
    const [selectedGestion, setSelectedGestion] = useState(new Date().getFullYear());

    useEffect(() => {
        loadLicencias();
    }, [selectedGestion]);

    const loadLicencias = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/jefatura/licencias/docentes?gestion=${selectedGestion}`);
            const data = await response.json();
            setLicencias(data.licencias || []);
        } catch (error) {
            console.error('Error:', error);
            setLicencias([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (licencia, action) => {
        setSelectedLicencia(licencia);
        setModalAction(action);
        setObservacion('');
        setShowModal(true);
    };

    const handleConfirm = async () => {
        if (!selectedLicencia) return;
        try {
            await fetch(`${API_URL}/jefatura/licencias/docentes/${selectedLicencia.id_licencia}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    estado: modalAction,
                    observaciones_jefatura: observacion || null
                })
            });
            setShowModal(false);
            setSelectedLicencia(null);
            setObservacion('');
            loadLicencias();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const getEstadoLabel = (estado) => {
        switch (estado) {
            case 'pendiente': return 'Pendiente';
            case 'preaprobado': return 'Preaprobado';
            case 'aprobado': return 'Aprobado';
            case 'observado': return 'Observado';
            case 'aprobado_direccion': return 'Aprobado';
            case 'rechazado_direccion': return 'Rechazado';
            default: return 'Pendiente';
        }
    };

    const getEstadoColor = (estado) => {
        switch (estado) {
            case 'pendiente': return 'bg-gray-200 text-gray-700';
            case 'preaprobado': return 'bg-yellow-100 text-yellow-800';
            case 'aprobado': return 'bg-green-100 text-green-700';
            case 'observado': return 'bg-red-100 text-red-800';
            case 'aprobado_direccion': return 'bg-green-100 text-green-700';
            case 'rechazado_direccion': return 'bg-red-300 text-red-900';
            default: return 'bg-gray-200 text-gray-700';
        }
    };

    const getModalTitle = () => {
        return modalAction === 'preaprobado' ? 'Preaprobar Licencia' : 'Rechazar Licencia';
    };

    const getModalMessage = () => {
        return modalAction === 'preaprobado'
            ? 'Introduzca un comentario para la preaprobación de la licencia (opcional).'
            : 'Introduzca el motivo del rechazo de la solicitud de licencia.';
    };

    const filteredLicencias = licencias.filter(l =>
        searchTerm === '' ||
        l.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.asignatura?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.tipo_permiso?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-6 text-center">Cargando...</div>;

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Jefatura de Carrera &gt; Licencias</h1>
            </div>

            {/* Toolbar y Filtros */}
            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">

                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <select
                            value={selectedGestion}
                            onChange={(e) => setSelectedGestion(parseInt(e.target.value))}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {[2024, 2025, 2026, 2027].map(g => (
                                <option key={g} value={g}>Gestión {g}</option>
                            ))}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>

                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                        <button onClick={() => {
                            const headers = ['No', 'Docente', 'Asignatura', 'Tipo Permiso', 'Fecha Permiso', 'Hora Permiso', 'Fecha Reemplazo/Compensación', 'Hora Reemplazo/Compensación', 'Docente Reemplazo', 'Sala Compensación', 'Estado', 'Observaciones Jefatura'];
                            const rows = filteredLicencias.map((l, i) => [
                                i + 1,
                                `${l.nombre || ''} ${l.apellido || ''}`.trim(),
                                l.asignatura || '',
                                l.tipo_permiso || '',
                                l.fecha_permiso_desde ? new Date(l.fecha_permiso_desde).toLocaleDateString() : '',
                                l.hora_permiso_inicio ? `${l.hora_permiso_inicio} - ${l.hora_permiso_fin || ''}` : '',
                                l.tipo_permiso === 'reemplazo'
                                    ? (l.fecha_reemplazo_desde ? new Date(l.fecha_reemplazo_desde).toLocaleDateString() : '-')
                                    : (l.fecha_compensacion_desde ? new Date(l.fecha_compensacion_desde).toLocaleDateString() : '-'),
                                l.tipo_permiso === 'reemplazo'
                                    ? (l.hora_reemplazo_inicio ? `${l.hora_reemplazo_inicio} - ${l.hora_reemplazo_fin || ''}` : '-')
                                    : (l.hora_compensacion_inicio ? `${l.hora_compensacion_inicio} - ${l.hora_compensacion_fin || ''}` : '-'),
                                l.docente_reemplazo_nombre || '-',
                                l.sala_compensacion || '-',
                                l.estado || 'pendiente',
                                l.observaciones_jefatura || ''
                            ]);
                            const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
                            const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
                            const link = document.createElement('a');
                            link.href = URL.createObjectURL(blob);
                            link.download = `licencias_docentes_${new Date().toISOString().split('T')[0]}.csv`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(link.href);
                        }} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm" title="Descargar Excel">
                            <Download size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Filtros */}
            {/* <div className="mb-4 flex flex-wrap gap-3">
                <select
                    value={selectedGestion}
                    onChange={(e) => setSelectedGestion(parseInt(e.target.value))}
                    className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm"
                >
                    {[2024, 2025, 2026, 2027].map(g => (
                        <option key={g} value={g}>Gestión {g}</option>
                    ))}
                </select>
                <div className="flex-1 max-w-md relative">
                    <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full" />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                </div>
                <button onClick={() => {
                    const headers = ['No', 'Docente', 'Asignatura', 'Tipo Permiso', 'Fecha Permiso', 'Hora Permiso', 'Fecha Reemplazo/Compensación', 'Hora Reemplazo/Compensación', 'Docente Reemplazo', 'Sala Compensación', 'Estado', 'Observaciones Jefatura'];
                    const rows = filteredLicencias.map((l, i) => [
                        i + 1,
                        `${l.nombre || ''} ${l.apellido || ''}`.trim(),
                        l.asignatura || '',
                        l.tipo_permiso || '',
                        l.fecha_permiso_desde ? new Date(l.fecha_permiso_desde).toLocaleDateString() : '',
                        l.hora_permiso_inicio ? `${l.hora_permiso_inicio} - ${l.hora_permiso_fin || ''}` : '',
                        l.tipo_permiso === 'reemplazo'
                            ? (l.fecha_reemplazo_desde ? new Date(l.fecha_reemplazo_desde).toLocaleDateString() : '-')
                            : (l.fecha_compensacion_desde ? new Date(l.fecha_compensacion_desde).toLocaleDateString() : '-'),
                        l.tipo_permiso === 'reemplazo'
                            ? (l.hora_reemplazo_inicio ? `${l.hora_reemplazo_inicio} - ${l.hora_reemplazo_fin || ''}` : '-')
                            : (l.hora_compensacion_inicio ? `${l.hora_compensacion_inicio} - ${l.hora_compensacion_fin || ''}` : '-'),
                        l.docente_reemplazo_nombre || '-',
                        l.sala_compensacion || '-',
                        l.estado || 'pendiente',
                        l.observaciones_jefatura || ''
                    ]);
                    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
                    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = `licencias_docentes_${new Date().toISOString().split('T')[0]}.csv`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                }} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm" title="Descargar Excel">
                    <Download size={20} />
                </button>
            </div> */}

            {/* Tabla de licencias */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">No</th>
                                <th className="px-3 py-3 text-center font-semibold border">Docente</th>
                                <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                                <th className="px-3 py-3 text-center font-semibold border">Tipo Permiso</th>
                                <th className="px-3 py-3 text-center font-semibold border">Fecha Permiso</th>
                                <th className="px-3 py-3 text-center font-semibold border">Hora Permiso</th>
                                <th className="px-3 py-3 text-center font-semibold border">Fecha Reemplazo/ Compensación</th>
                                <th className="px-3 py-3 text-center font-semibold border">Hora Reemplazo/ Compensación</th>
                                <th className="px-3 py-3 text-center font-semibold border">Docente Reemplazo</th>
                                <th className="px-3 py-3 text-center font-semibold border">Sala Compensación</th>
                                <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                                <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                <th className="px-3 py-3 text-center font-semibold border">Observaciones Jefatura de Carrera</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLicencias.length === 0 ? (
                                <tr><td colSpan="13" className="px-4 py-8 text-center text-gray-500">No hay licencias registradas</td></tr>
                            ) : (
                                filteredLicencias.map((l, index) => (
                                    <tr key={l.id_licencia} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-center border">{index + 1}</td>
                                        <td className="px-3 py-2 border">{l.nombre || '-'} {l.apellido || ''}</td>
                                        <td className="px-3 py-2 border">{l.asignatura || '-'}</td>
                                        <td className="px-2 py-3 text-center capitalize border">{l.tipo_permiso || '-'}</td>
                                        <td className="px-2 py-3 text-center border">
                                            {l.fecha_permiso_desde ? new Date(l.fecha_permiso_desde).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-2 py-3 text-center border">
                                            {l.hora_permiso_inicio || '-'}
                                        </td>
                                        <td className="px-2 py-3 text-center border">
                                            {l.tipo_permiso === 'reemplazo'
                                                ? (l.fecha_reemplazo_desde ? new Date(l.fecha_reemplazo_desde).toLocaleDateString() : '-')
                                                : (l.fecha_compensacion_desde ? new Date(l.fecha_compensacion_desde).toLocaleDateString() : '-')
                                            }
                                        </td>
                                        <td className="px-2 py-3 text-center border">
                                            {l.tipo_permiso === 'reemplazo'
                                                ? (l.hora_reemplazo_inicio || '-')
                                                : (l.hora_compensacion_inicio || '-')
                                            }
                                        </td>
                                        <td className="px-3 py-2 border">{l.docente_reemplazo_nombre?.trim() || '-'}</td>
                                        <td className="px-3 py-2 border">{l.sala_compensacion || '-'}</td>
                                        {/* Acciones - Siempre verde y rojo */}
                                        <td className="px-3 py-2 border">
                                            <div className="flex justify-center gap-1">
                                                <button
                                                    onClick={() => handleOpenModal(l, 'preaprobado')}
                                                    className="p-2 rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors"
                                                    title="Preaprobar"
                                                    disabled={l.estado === 'aprobado'}
                                                >
                                                    <Check size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleOpenModal(l, 'observado')}
                                                    className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                                                    title="Rechazar/Observar"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        </td>
                                        {/* Estado */}
                                        <td className="px-2 py-3 text-center border">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(l.estado)}`}>
                                                {getEstadoLabel(l.estado)}
                                            </span>
                                        </td>
                                        {/* Observaciones Jefatura */}
                                        <td className="px-3 py-2 border">{l.observaciones_jefatura || '-'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal para Aprobar/Rechazar */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-xl font-bold text-blue-900">{getModalTitle()}</h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-gray-700 mb-4">
                                {getModalMessage()}
                            </p>
                            <textarea
                                value={observacion}
                                onChange={(e) => setObservacion(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                rows={4}
                                placeholder="Escriba aquí..."
                            />
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t">
                            <button
                                onClick={handleConfirm}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
